/**
 * @fileoverview Registry of bandit experiments currently mounted
 * somewhere on the page — the piece `useBanditArm` itself doesn't
 * track, since `BanditContext` eagerly caches every experiment
 * registered for the course rather than only the ones actually
 * rendered right now.
 *
 * Purpose-built for the admin `ExperimentControlPanel` overlay: "which
 * experiments does the CURRENT page actually show" is exactly the
 * question a page-level control panel needs answered, and course-wide
 * registration would surface controls for experiments that have
 * nothing to do with the page the admin is looking at.
 *
 * Reference-counted (not a plain `Set`) so two components mounting the
 * same experiment id — unusual today, but not prevented — don't have
 * the second unmount hide the first's still-live registration.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Value provided by {@link ActiveExperimentsProvider}. */
export interface ActiveExperimentsContextValue {
  /** Experiment IDs with at least one live registration on this page. */
  activeExperimentIds: readonly string[];
  /** Registers one mount of `experimentId`; call the returned unregister on cleanup. */
  register: (experimentId: string) => () => void;
}

const ActiveExperimentsContext =
  createContext<ActiveExperimentsContextValue | null>(null);

/**
 * Reads {@link ActiveExperimentsContext}.
 *
 * @return The current registry, or a no-op stand-in outside a provider
 *   (so a stray `useBanditArm` call in an untested tree never throws —
 *   it simply isn't visible to any control panel).
 */
export function useActiveExperiments(): ActiveExperimentsContextValue {
  return (
    useContext(ActiveExperimentsContext) ?? {
      activeExperimentIds: [],
      register: () => () => {},
    }
  );
}

/**
 * Provider component; see module docs.
 *
 * @param props.children - Subtree whose `useBanditArm` calls register
 *   here.
 * @return Provider element.
 */
export function ActiveExperimentsProvider({
  children,
}: {
  children: ReactNode;
}) {
  // experimentId -> number of currently-mounted registrations.
  const [counts, setCounts] = useState<Record<string, number>>({});

  // Stable across count changes: callers register in an effect keyed on it,
  // so a new identity per registration would re-register forever.
  const register = useCallback((experimentId: string) => {
    setCounts(prev => ({
      ...prev,
      [experimentId]: (prev[experimentId] ?? 0) + 1,
    }));
    return () => {
      setCounts(prev => ({
        ...prev,
        [experimentId]: Math.max(0, (prev[experimentId] ?? 1) - 1),
      }));
    };
  }, []);

  const value = useMemo<ActiveExperimentsContextValue>(
    () => ({
      activeExperimentIds: Object.keys(counts).filter(id => counts[id] > 0),
      register,
    }),
    [counts, register],
  );

  return (
    <ActiveExperimentsContext.Provider value={value}>
      {children}
    </ActiveExperimentsContext.Provider>
  );
}
